import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { RootState } from './store'

export interface PriceInfo {
  price_cents: number
  sale_price_cents: number | null
  on_sale: boolean
  product_name: string
  product_size: string | null
  store_name: string
}

interface PricesState {
  byFoodId: Record<number, PriceInfo | null>
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
}

const initialState: PricesState = {
  byFoodId: {},
  status: 'idle',
}

export const fetchPricesForFoods = createAsyncThunk(
  'prices/fetchForFoods',
  async (foodIds: number[], { getState }) => {
    const state = getState() as RootState
    const known = state.prices.byFoodId
    const missing = foodIds.filter((id) => !(id in known))
    if (missing.length === 0) return {}

    const query = missing.map((id) => `food_ids[]=${id}`).join('&')
    const res = await fetch(`/api/prices?${query}`, {
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error('Failed to fetch prices')
    const data = await res.json()
    return data.prices as Record<string, PriceInfo | null>
  }
)

const pricesSlice = createSlice({
  name: 'prices',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPricesForFoods.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchPricesForFoods.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const incoming = action.payload as Record<string, PriceInfo | null>
        Object.entries(incoming).forEach(([id, info]) => {
          state.byFoodId[Number(id)] = info
        })
      })
      .addCase(fetchPricesForFoods.rejected, (state) => {
        state.status = 'failed'
      })
  },
})

export default pricesSlice.reducer

export const formatPrice = (cents: number): string =>
  `$${(cents / 100).toFixed(2)}`
